/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { setting } from '../../../../setting';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ArticleService } from './article.service';
import { HttpErrorResponse } from '@angular/common/http';
import { HEADER_ORIGIN_STORAGE_KEY } from 'app/shared/utils/article-header-origin';

interface RelatedDestination {
  typeArticle: 'big' | 'small';
  id: number;
  image: string;
  title: string;
  description: string;
}

interface UsefulDataPoint {
  value: string;
  unit: string;
  description: string;
}

// Mockup de "Datos útiles" (aside del acordeón, ver template): todavía no viene del back, así
// que por ahora es data fija por título de artículo, buscada a mano (ver fuentes en la
// conversación). Cuando el back lo traiga armado, esto se reemplaza por lo que devuelva la API
// — el título es la única clave disponible hoy para saber a qué artículo corresponde. Array (no
// objeto con el título como key) para no chocar con la regla de naming-convention del lint.
const USEFUL_DATA_ENTRIES: { title: string; data: UsefulDataPoint[] }[] = [
  {
    title: 'CONOCÉ CALAFATE',
    data: [
      { value: '250', unit: 'Km²', description: 'Superficie total del glaciar Perito Moreno' },
      { value: '30', unit: 'Km', description: 'Longitud del glaciar' },
      { value: '5', unit: 'Km', description: 'Ancho del frente de hielo sobre el lago' },
      { value: '74', unit: 'm', description: 'Altura del frente de hielo sobre el lago Argentino' },
      { value: '78', unit: 'Km', description: 'Distancia desde El Calafate hasta el glaciar' },
    ],
  },
  {
    title: 'CONOCÉ BARILOCHE',
    data: [
      { value: '557', unit: 'Km²', description: 'Superficie del lago Nahuel Huapi' },
      { value: '2388', unit: 'm', description: 'Altura del Cerro Catedral' },
      { value: '438', unit: 'm', description: 'Profundidad máxima del lago Nahuel Huapi' },
      { value: '120', unit: 'Km', description: 'Kilómetros esquiables en el Cerro Catedral' },
      { value: '19', unit: 'Km', description: 'Distancia del Cerro Catedral al centro de Bariloche' },
    ],
  },
  {
    title: 'CONOCÉ IGUAZÚ',
    data: [
      { value: '275', unit: 'saltos', description: 'Cantidad de saltos de agua que forman las cataratas' },
      { value: '82', unit: 'm', description: 'Altura de la Garganta del Diablo, el salto más alto' },
      { value: '2,7', unit: 'Km', description: 'Extensión total del sistema de cataratas' },
      { value: '1750', unit: 'm³/s', description: 'Caudal medio del sistema de cataratas' },
      { value: '80', unit: '%', description: 'Porcentaje de las cataratas del lado argentino' },
    ],
  },
  {
    title: 'CERRO CASTOR',
    data: [
      { value: '1057', unit: 'm', description: 'Altura de la cima de Cerro Castor' },
      { value: '34', unit: 'pistas', description: 'Cantidad de pistas de esquí' },
      { value: '772', unit: 'm', description: 'Desnivel esquiable de la montaña' },
      { value: '26', unit: 'Km', description: 'Distancia desde Ushuaia' },
      { value: '-18', unit: '°C', description: 'Temperatura mínima extrema registrada' },
    ],
  },
  {
    title: 'EL TURISMO MÉDICO',
    data: [
      { value: '25.000', unit: 'pacientes', description: 'Extranjeros que se atienden por año en Argentina' },
      { value: '300', unit: 'M U$D', description: 'Ingresos anuales generados por turismo médico' },
      { value: '50', unit: '%', description: 'Ahorro promedio en cirugías frente a EE.UU.' },
      { value: '75', unit: '%', description: 'Pacientes que llegan de países limítrofes' },
      {
        value: '6',
        unit: 'ciudades',
        description: 'Principales destinos: Buenos Aires, Córdoba, Rosario, Bariloche, Salta y Mendoza',
      },
    ],
  },
  {
    title: 'LAS CASAS DEL TANGO',
    data: [
      { value: '1858', unit: 'año', description: 'Fundación del Café Tortoni, ícono cultural porteño' },
      { value: '1969', unit: 'año', description: 'Fundación de El Viejo Almacén, casa de tango en San Telmo' },
      { value: '100+', unit: 'milongas', description: 'Milongas activas en la actualidad en Buenos Aires' },
      { value: '1880', unit: 'década', description: 'Cuando el tango empezó a popularizarse en la ciudad' },
      { value: 'XIX', unit: 'siglo', description: 'Siglo en el que nació el tango en los arrabales porteños' },
    ],
  },
  {
    title: 'MESSI DEL VINO',
    data: [
      {
        value: '100',
        unit: 'puntos',
        description: 'Puntaje perfecto que obtuvieron sus vinos según la crítica internacional',
      },
      { value: '18', unit: 'etiquetas', description: 'Vinos suyos que alcanzaron los 100 puntos' },
      { value: '2007', unit: 'año', description: 'Desde cuándo es director de enología de Catena Zapata' },
      { value: '20', unit: 'locales', description: 'Restaurantes y bares que opera en Mendoza' },
      { value: '1200', unit: 'm', description: 'Altitud promedio de los viñedos del Valle de Uco' },
    ],
  },
  {
    title: 'PUERTO MADRYN',
    data: [
      {
        value: '2.110',
        unit: 'ballenas',
        description: 'Ejemplares de ballena franca austral censados en la temporada 2025',
      },
      { value: '826', unit: 'crías', description: 'Crías de ballena registradas en el último censo' },
      {
        value: '15',
        unit: 'Km',
        description: 'Distancia a la playa El Doradillo, donde se avistan desde la costa',
      },
      { value: '8', unit: 'meses', description: 'Duración de la temporada de avistaje (mayo a diciembre)' },
      {
        value: '104',
        unit: 'Km',
        description: 'Distancia a Puerto Pirámides, único lugar habilitado para avistaje embarcado',
      },
    ],
  },
  {
    title: 'EL FAM EN SÍNTESIS',
    data: [
      {
        value: '8',
        unit: 'DÍAS',
        description: 'de inmersión en el destino'
      },
      {
        value: '6',
        unit: 'DESTINOS',
        description: 'Santa Marta · Tayrona · Ciénaga · Aracataca · Ciénaga Grande · Barranquilla'
      },
      {
        value: '9',
        unit: 'ALOJAMIENTOS',
        description: 'conocidos de primera mano'
      },
      {
        value: '3',
        unit: 'ALOJAMIENTOS',
        description: 'donde vivimos la experiencia como huéspedes'
      },
      {
        value: '1',
        unit: 'WORKSHOP',
        description: 'con rondas de negocios y nuevos contactos comerciales'
      },
      // {
      //   value: 'UNA RED DE PARTNERS',
      //   unit: '',
      //   description: 'operadores · hoteles · burós · proveedores · organismos de promoción'
      // },
      // {
      //   value: 'NUEVAS OPORTUNIDADES',
      //   unit: '',
      //   description: 'cultura · literatura · naturaleza · comunidad · gastronomía · Caribe'
      // },
      // {
      //   value: 'CONTENIDO PROPIO',
      //   unit: '',
      //   description: 'fotografías y videos para promoción, capacitación y comercialización'
      // },
      // {
      //   value: 'CONOCER · CONECTAR · DESARROLLAR',
      //   unit: '',
      //   description: 'Del conocimiento del destino a nuevas oportunidades comerciales.'
      // },
    ]
  },
  {
    title: 'MOEBIUS DMC EN EL FIT',
    data: [
      {
        value: '8',
        unit: 'DÍAS',
        description: 'de inmersión en el destino'
      },
      {
        value: '6',
        unit: 'DESTINOS',
        description: 'Santa Marta · Tayrona · Ciénaga · Aracataca · Ciénaga Grande · Barranquilla'
      },
      {
        value: '9',
        unit: 'ALOJAMIENTOS',
        description: 'conocidos de primera mano'
      },
      {
        value: '3',
        unit: 'ALOJAMIENTOS',
        description: 'donde vivimos la experiencia como huéspedes'
      },
      {
        value: '1',
        unit: 'WORKSHOP',
        description: 'con rondas de negocios y nuevos contactos comerciales'
      },
      // {
      //   value: 'UNA RED DE PARTNERS',
      //   unit: '',
      //   description: 'operadores · hoteles · burós · proveedores · organismos de promoción'
      // },
      // {
      //   value: 'NUEVAS OPORTUNIDADES',
      //   unit: '',
      //   description: 'cultura · literatura · naturaleza · comunidad · gastronomía · Caribe'
      // },
      // {
      //   value: 'CONTENIDO PROPIO',
      //   unit: '',
      //   description: 'fotografías y videos para promoción, capacitación y comercialización'
      // },
      // {
      //   value: 'CONOCER · CONECTAR · DESARROLLAR',
      //   unit: '',
      //   description: 'Del conocimiento del destino a nuevas oportunidades comerciales.'
      // },
    ]
  },
];

@Component({
  selector: 'usb-article',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss']
})
export class ArticleComponent implements OnInit, OnDestroy {

  public getClientsSettingAppAuth: any;
  public routeUploadFile!: string;
  public wspNumber: any;
  public articleId!: string;
  public typeArticle!: string;
  public activeArticle!: number;
  public clientId!: number;

  public article: any;
  public images: any[] = [];
  public headerArr: any[] = [];

  article1Visible = true;
  article2Visible = false;
  article3Visible = false;

  showChange = false;

  visibleCount = 0;

  loadedVisual = false;
  galeryImageLoad = false;

  p = 0;

  lightboxOpen = false;
  lightboxIndex = 0;

  showScrollToTop = false;
  appLoading = true;
  splashFinish = false;

  // Qué secciones del acordeón de "artículos" están desplegadas (ver toggleTextSection()).
  public expandedTextIndices: Set<number> = new Set<number>();

  // Imagen/título/descripción que manda destination-big/destination-small al navegar (ver
  // articleRedirect() en esos componentes), usados para el header del artículo. Si se llega
  // acá de otra forma (ej. desde "artículos relacionados") no hay state y el header no se muestra.
  public headerFromOrigin: { image?: string; title?: string; description?: string } | null = null;

  // Slider del hero (inspirado en https://vista-editorial-gallery.lovable.app/#): combina la
  // foto de portada (headerFromOrigin) con las de la galería propia del artículo, para que se
  // note el efecto de crossfade en vez de una sola imagen fija. Ver buildHeroImages().
  public heroImages: string[] = [];
  public heroActiveIndex = 0;
  private heroIntervalId: any;

  // "Artículos relacionados": 3 destinos elegidos semi-azarosamente de destination-big/small
  // (no de article[0].relationated), para invitar a seguir navegando por el sitio en vez de
  // repetir la data propia del artículo. Se arma una sola vez por artículo (ver
  // buildRelatedDestinations()), no es un getter, para no recalcularlo en cada ciclo de CD.
  public relatedDestinations: RelatedDestination[] = [];

  // "Datos útiles" del aside — mockup fijo por título (ver USEFUL_DATA_ENTRIES arriba) hasta que
  // el back lo traiga armado. Solo se muestra para los títulos que tienen entrada en el mockup.
  public usefulData: UsefulDataPoint[] = [];

  private routeSubscription!: Subscription;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(

              public _router: Router,
              public activatedRoute: ActivatedRoute,
              private articleService: ArticleService,
              private cdr: ChangeDetectorRef
             ) {



              }


  ngOnInit(): void {
    this.article = [];
    this.headerArr = [];
    this.images = [];
    this.routeUploadFile = setting.routeUploadFile;
    this.getClientsSettingAppAuth =  JSON.parse((localStorage.getItem(setting.name)));
    let wspNumber = this.getClientsSettingAppAuth.wsp_phone_number;

    // Eliminar el signo + y los espacios en blanco
    wspNumber = wspNumber.replace(/\+/g, '').replace(/\s/g, '');

    this.wspNumber = wspNumber;
    this.suscriptionParam();

  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
    this.stopHeroSlider();
  }

  suscriptionParam(showChange?: boolean, articleId?: number): void {

    this.routeSubscription = this.activatedRoute.params
    .pipe(takeUntil(this._unsubscribeAll))
    .subscribe(params => {

      this.typeArticle = params['typeArticle'];
      this.articleId = params['id'];
      this.activeArticle = Number(params['active']);
      this.clientId = Number(params['clientId']);

      // history.state no sobrevive a un refresh (Angular lo pisa en la navegación inicial del
      // router). sessionStorage sí, así que lo usamos para esto — validando que sea el mismo
      // artículo al que apunta la ruta actual, para no arrastrar datos de una tarjeta vieja.
      this.headerFromOrigin = this.readHeaderFromOrigin();
      this.usefulData = this.buildUsefulData(this.headerFromOrigin?.title);

            if(this.showChange === false) {
              if(this.typeArticle === 'small') {
                if(this.article) {
                  this.dataArticle(1, Number(this.articleId), this.clientId);
                  this.appLoading = false;
                  this.loadedVisual = true;
                }
              }
              if(this.typeArticle === 'big') {
                if(this.article) {
                  this.dataArticle(2, Number(this.articleId), this.clientId);
                  this.appLoading = false;
                  this.loadedVisual = true;
                }
              }
              if(this.typeArticle === 'segment') {
                if(this.article) {
                  this.dataArticle(3, Number(this.articleId), this.clientId);
                  this.appLoading = false;
                  this.loadedVisual = true;
                }

              }
               if(this.typeArticle === 'aboutUs') {
                if(this.article) {
                  this.dataArticle(4, Number(this.articleId), this.clientId);
                  this.appLoading = false;
                  this.loadedVisual = true;
                }
              }

            // this.cdr.detectChanges();
            }
            if (showChange === true) {

              const url = `/article/${this.typeArticle}/${this.articleId}/${this.clientId}/${articleId}`;

              if(this.typeArticle === 'small') {
                this.appLoading = true;
                this._router.navigate([url]);
                setTimeout(() => {
                  this.appLoading = false;
                  this.loadedVisual = true;
                }, 500);
              }
              if(this.typeArticle === 'big') {
                this.appLoading = true;
                this._router.navigate([url]);
                setTimeout(() => {
                  this.appLoading = false;
                  this.loadedVisual = true;
                }, 500);
              }
              if(this.typeArticle === 'segment') {
                this.appLoading = true;
                this._router.navigate([url]);
                setTimeout(() => {
                  this.appLoading = false;
                  this.loadedVisual = true;
                }, 500);
              }

            // this.cdr.detectChanges();
            }
          });
  }

  dataArticle(type: number, id: number, clientId: number): void {
    if(type && id && clientId) {
      this.articleService.getArticle(type, id, clientId).subscribe({
        next: (res: any) => {
          if(res && res.det) {
            this.article = [res.det];
            this.articleMap();
            this.articleDataRender();
          }
        },
        error: (error: HttpErrorResponse) => {
          console.log(error);
        }
      });
    } else {
      console.log('Error al procesar información de consulta');
    }

  }

  articleMap(): void {
    this.article = this.article.map((art: any) => {
      // Los artículos que llegan desde el footer (segment/aboutUs) no siempre traen todos estos
      // campos (ej. "relationated"), y JSON.parse(undefined) tira una excepción que cortaba el
      // resto del render (accordeón, galería, y los relacionados nuevos) antes de llegar a
      // articleDataRender(). Con fallback seguro, un campo faltante no rompe el resto.
      art.imageHeader = [this.safeJsonParse(art.imageHeader, {})];
      art.photos = [this.safeJsonParse(art.photos, {})];
      art.relationated = [this.safeJsonParse(art.relationated, { info: [] })];
      art.text = [this.safeJsonParse(art.text, {})];
      art.title = [this.safeJsonParse(art.title, { info: [] })];
      art.visible = this.articleId;
      return art;
    });
  }

  articleDataRender(): void {
    const photosObject = this.article[0]?.photos[0];
    // Las fotos de "relationated" quedaron sin uso al mover los relacionados a destination-big/
    // small (ver goToRelatedDestination()). Como tienen otro formato/relación de aspecto que las
    // de la galería propia, se intercalan (no se pegan al final) para que el mosaico se vea más
    // variado en vez de un bloque parejo seguido de uno distinto.
    this.images = this.interleaveArrays(Object.values(photosObject || {}), this.getRelationatedImages());
    const textArticles = this.article[0]?.text[0];
    this.article[0].text = Object.values(textArticles);
    this.headerArr = this.article[0]?.imageHeader[0];
    this.findMaxVisibleIndex();
    this.expandedTextIndices.clear();
    this.relatedDestinations = this.buildRelatedDestinations();
    this.buildHeroImages();
  }

  findMaxVisibleIndex(): void {
    if (this.article && this.article[0] && this.article[0].relationated) {
      this.article[0].maxVisibleIndex = Math.max(...this.article[0].relationated.map((relation, index) => index));
    }
  }

  showArticle(articleId: number): void {
    this.showChange = true;
    this.suscriptionParam(this.showChange, articleId);


      // Desplazarse hacia arriba de la pantalla
      window.scrollTo({ top: 0, behavior: 'smooth' });

  }

  toggleTextSection(index: number): void {
    const wasExpanded = this.expandedTextIndices.has(index);

    if (wasExpanded) {
      this.expandedTextIndices.delete(index);
      // Si el contenido quedaba scrolleado hacia abajo, al cerrar la página encoge y la vista
      // queda apuntando a cualquier lado. Volvemos a la parte de arriba de esta sección, con
      // 220px de margen para no quedar tapados por el header fijo de arriba.
      setTimeout(() => {
        const element = document.getElementById(`article-accordion-item-${index}`);
        if (!element) {
          return;
        }
        const headerOffset = 220;
        const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      }, 350);
    } else {
      this.expandedTextIndices.add(index);
    }
  }

  isTextExpanded(index: number): boolean {
    if (!this.isAccordionEnabled()) {
      return true;
    }
    return this.expandedTextIndices.has(index);
  }

  // Los artículos que vienen del footer (segment/aboutUs) son contenido informativo simple: se
  // muestran completos, sin el comportamiento de acordeón ni el botón "Ver más/Ver menos".
  isAccordionEnabled(): boolean {
    return this.typeArticle !== 'segment' && this.typeArticle !== 'aboutUs';
  }

  hasMultipleSections(): boolean {
    return (this.article?.[0]?.title?.[0]?.info?.length || 0) > 1;
  }

  padSectionNumber(index: number): string {
    const n = index + 1;
    return n < 10 ? `0${n}` : `${n}`;
  }

  isSecondVisibleElement(index: number): boolean {
    // Resetea el contador de elementos visibles cada vez que se recalcula la vista
    this.visibleCount = 0;

    for (let i = 0; i < this.article[0].relationated[0]?.info.length; i++) {
      if (this.activeArticle !== i) {
        this.visibleCount++;
        if (this.visibleCount === 2 && i === index) {
          return true;
        }
      }
    }
    return false;
  }

  imageCharge(event: any): void {
      this.loadedVisual = true;
      this.appLoading = false;
  }

  navigateHome(): void {
    this._router.navigate(['/home']);
  }

  scrollToArticleStart(): void {

    const element = document.getElementById('article-start');

    if(element) {
      const headerOffset = 220;
      const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;

      window.scrollTo({ top, behavior: 'smooth' });
    }

  }

  goToHeroImage(index: number): void {
    this.heroActiveIndex = index;
    // Reinicia el auto-rotado para no cortarlo a mitad de camino después de una selección manual.
    this.stopHeroSlider();
    if (this.heroImages.length > 1) {
      this.startHeroSlider();
    }
  }

  trackByHeroImage(index: number, img: string): string {
    return img;
  }

  trackByUsefulData(index: number): number {
    return index;
  }

  // Igual patrón que articleRedirect() en destination-big/destination-small: manda la
  // imagen/título/descripción del destino elegido para que el header del artículo destino la
  // use directo, sin volver a pedirla a la API.
  goToRelatedDestination(item: RelatedDestination): void {
    const active = 0;
    sessionStorage.setItem(HEADER_ORIGIN_STORAGE_KEY, JSON.stringify({
      typeArticle: item.typeArticle,
      id: item.id,
      image: item.image,
      title: item.title,
      description: item.description
    }));
    this._router.navigate([`/article/${item.typeArticle}/${item.id}/${this.clientId}/${active}`]);
  }

  loadImagenGalery(): void {
    setTimeout(() => {
      this.appLoading = false;
    }, 500);
  }

  openLightbox(index: number): void {
    this.lightboxIndex = index;
    this.lightboxOpen = true;
  }

  closeLightbox(event?: MouseEvent): void {
    event?.stopPropagation();
    this.lightboxOpen = false;
  }

  lightboxPrev(event: MouseEvent): void {
    event.stopPropagation();
    this.lightboxIndex = (this.lightboxIndex - 1 + this.images.length) % this.images.length;
  }

  lightboxNext(event: MouseEvent): void {
    event.stopPropagation();
    this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
  }

  @HostListener('document:keydown.escape')
  onLightboxEscape(): void {
    if (this.lightboxOpen) {
      this.lightboxOpen = false;
    };
  };

  @HostListener('document:keydown.arrowleft')
  onLightboxArrowLeft(): void {
    if (this.lightboxOpen) {
      this.lightboxIndex = (this.lightboxIndex - 1 + this.images.length) % this.images.length;
    }
  }

  @HostListener('document:keydown.arrowright')
  onLightboxArrowRight(): void {
    if (this.lightboxOpen) {
      this.lightboxIndex = (this.lightboxIndex + 1) % this.images.length;
    }
  }

  // sessionStorage escrito por articleRedirect() en destination-big/destination-small (ver esos
  // componentes). Solo se usa si coincide con el artículo que la ruta actual está mostrando.
  private readHeaderFromOrigin(): { image?: string; title?: string; description?: string } | null {
    const stored = sessionStorage.getItem(HEADER_ORIGIN_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    try {
      const parsed = JSON.parse(stored);
      const matchesCurrentArticle = parsed?.typeArticle === this.typeArticle
        && String(parsed?.id) === String(this.articleId);
      return matchesCurrentArticle ? parsed : null;
    } catch {
      return null;
    }
  }

  // Mockup de "Datos útiles": busca por título (sin mayúsculas/espacios de más) en el listado
  // fijo de arriba. Si el título del artículo actual no tiene entrada, no se muestra nada.
  private buildUsefulData(title: string | undefined): UsefulDataPoint[] {
    if (!title) {
      return [];
    }
    const normalizedTitle = title.trim().toUpperCase();
    const entry = USEFUL_DATA_ENTRIES.find((e) => e.title === normalizedTitle);
    return entry ? entry.data : [];
  }

  // Arma el pool de destinos desde section_destination_big/section_destination_small (misma
  // data que usan destination-big/destination-small), saca el artículo actual si aparece ahí, y
  // elige 3 al azar.
  private buildRelatedDestinations(): RelatedDestination[] {
    const bookingSettings = this.getClientsSettingAppAuth?.clients_setting_app_booking;
    if (!bookingSettings) {
      return [];
    }

    const pool: RelatedDestination[] = [
      ...this.mapDestinationInfo(bookingSettings.section_destination_big, 'big'),
      ...this.mapDestinationInfo(bookingSettings.section_destination_small, 'small')
    ];

    const withoutCurrentArticle = pool.filter((item) => !(
      item.typeArticle === this.typeArticle && String(item.id) === String(this.articleId)
    ));

    return this.pickRandom(withoutCurrentArticle, 3);
  }

  private mapDestinationInfo(rawSection: string | undefined, typeArticle: 'big' | 'small'): RelatedDestination[] {
    if (!rawSection) {
      return [];
    }

    try {
      const info = JSON.parse(rawSection)?.info || [];
      return (info as any[]).map((entry, index) => ({
        typeArticle,
        id: index + 1,
        image: this.routeUploadFile + entry.image,
        title: entry.title,
        description: entry.description
      }));
    } catch {
      return [];
    }
  }

  private pickRandom(items: RelatedDestination[], count: number): RelatedDestination[] {
    const pool = [...items];
    const picked: RelatedDestination[] = [];
    while (pool.length > 0 && picked.length < count) {
      const randomIndex = Math.floor(Math.random() * pool.length);
      picked.push(pool.splice(randomIndex, 1)[0]);
    }
    return picked;
  }

  private safeJsonParse<T>(value: string | null | undefined, fallback: T): T {
    if (!value) {
      return fallback;
    }
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  private getRelationatedImages(): string[] {
    const info = this.article[0]?.relationated[0]?.info || [];
    return (info as any[])
      .map((relation) => relation?.image)
      .filter((image): image is string => !!image);
  }

  private buildHeroImages(): void {
    this.stopHeroSlider();

    const galleryImages = (this.images || []).map((img: string) => this.routeUploadFile + img);
    const combined = this.headerFromOrigin?.image
      ? [this.headerFromOrigin.image, ...galleryImages]
      : galleryImages;

    // Sin duplicados (la portada puede repetirse en la galería) y con un tope razonable para
    // que el slider no quede eterno.
    this.heroImages = Array.from(new Set(combined)).slice(0, 6);
    this.heroActiveIndex = 0;

    if (this.heroImages.length > 1) {
      this.startHeroSlider();
    }
  }

  private startHeroSlider(): void {
    this.heroIntervalId = setInterval(() => {
      this.heroActiveIndex = (this.heroActiveIndex + 1) % this.heroImages.length;
    }, 5000);
  }

  private stopHeroSlider(): void {
    if (this.heroIntervalId) {
      clearInterval(this.heroIntervalId);
      this.heroIntervalId = undefined;
    }
  }

  private interleaveArrays<T>(a: T[], b: T[]): T[] {
    const result: T[] = [];
    const maxLength = Math.max(a.length, b.length);
    for (let i = 0; i < maxLength; i++) {
      if (i < a.length) {
        result.push(a[i]);
      }
      if (i < b.length) {
        result.push(b[i]);
      }
    }
    return result;
  }


  getfooterInformationLocation(location: string): string {

    let locationName = '';

    switch(location) {
      case 'CONOCÉ BARILOCHE': locationName = 'Bariloche'; break;
      case 'CONOCÉ IGUAZÚ': locationName = 'Iguazú'; break;
      case 'CONOCÉ CALAFATE': locationName = 'Calafate'; break;
      default: locationName = location;
    }

    return locationName;
  }

}
